<script setup>
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref } from 'vue'
import {Link, router, usePage} from '@inertiajs/vue3'
import { Award, ChevronRight, Clapperboard, Clock, Film, MessageCircle, MessageSquarePlus, Share2, Star, Trophy, Users, X, Zap } from 'lucide-vue-next'
import CastingCard from '@/Components/Casting/CastingCard.vue'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import {getCastingsUrl, getMainAuditionsUrl} from "@/Helpers/auditions";
import axios from 'axios'
import {trackFathomEvent} from "@/Helpers/analytics";
const StarPowerModal = defineAsyncComponent(() => import('@/Components/Reels/StarPowerModal.vue'))
const BuyStarsModal  = defineAsyncComponent(() => import('@/Components/Reels/BuyStarsModal.vue'))
const SharePopup     = defineAsyncComponent(() => import('@/Components/Common/SharePopup.vue'))

const props = defineProps({
    profile:             { type: Object,  required: true },
    stats:               { type: Object,  required: true },
    sponsoredAuditions:  { type: Object,  required: true },
    comments:            { type: Object,  required: true },
    sponsoredPerformers:  { type: Array,   default: () => [] },
    topPerformerAffinities: { type: Array,  default: () => [] },
    milestones:           { type: Array,   default: () => [] },
    pendingAuditions:     { type: Array,   default: () => [] },
    shoutouts:            { type: Array,   default: () => [] },
    canWriteShoutouts:    { type: Boolean, default: false },
    isOwner:              { type: Boolean, default: false },
    starPacks:            Array,
    performersLazy:       { type: Boolean, default: false },
    requestedCastings:    { type: Array,   default: () => [] },
    supportedCastings:    { type: Array,   default: () => [] },
    discoverCastings:     { type: Array,   default: () => [] },
})

const { props: sharedProps } = usePage()

const isUserLoggedIn = computed(() => !!(sharedProps.auth?.user?.id))
const isGoldUser     = computed(() => !!(sharedProps.auth?.is_gold))
const routePrefix    = computed(() => sharedProps.routePrefix || 'www')

const isStarPowerModalOpen = ref(false)
const isBuyStarsModalOpen = ref(false)
const walletBalance = ref(Number(sharedProps.auth?.user?.wallet_balance) || 0)

// ── Performer data (may be lazy-loaded when backend skips cold API call) ──
const sponsoredPerformersLocal    = ref([...props.sponsoredPerformers])
const topPerformerAffinitiesLocal = ref([...props.topPerformerAffinities])
const isPerformersLoading         = ref(props.performersLazy)

// ── Performers carousel auto-scroll ───────────────────────────────────────
const carouselEl        = ref(null)
const carouselPaused    = ref(false)
const carouselOffScreen = ref(false)
const carouselNeedsLoop = ref(false)
let carouselRaf      = null
let carouselObserver = null

const startCarousel = () => {
    const el = carouselEl.value
    if (!el) return

    // Pause RAF work when the carousel is scrolled out of view
    carouselObserver = new IntersectionObserver(
        ([entry]) => { carouselOffScreen.value = !entry.isIntersecting },
        { threshold: 0 }
    )
    carouselObserver.observe(el)

    let lastTime = null
    const SPEED = 40
    const tick = (now) => {
        if (!carouselPaused.value && !carouselOffScreen.value) {
            if (lastTime !== null) {
                el.scrollLeft += SPEED * (now - lastTime) / 1000
                if (el.scrollLeft >= el.scrollWidth / 2) {
                    el.scrollLeft -= el.scrollWidth / 2
                }
            }
            lastTime = now
        } else {
            lastTime = null
        }
        carouselRaf = requestAnimationFrame(tick)
    }
    carouselRaf = requestAnimationFrame(tick)
}

// ── Shoutout spotlight cycling ────────────────────────────────────────────
const shoutoutIndex   = ref(0)
const shoutoutVisible = ref(true)
let shoutoutTimer = null

const cycleShoutout = () => {
    if (props.shoutouts.length <= 1) return
    shoutoutTimer = setInterval(() => {
        shoutoutVisible.value = false
        setTimeout(() => {
            shoutoutIndex.value = (shoutoutIndex.value + 1) % props.shoutouts.length
            shoutoutVisible.value = true
        }, 400)
    }, 6000)
}

const activeShoutout = computed(() => props.shoutouts[shoutoutIndex.value] ?? null)

// "A message for you" — only when the viewer is the profile owner and not an admin
const shoutoutEyebrow = computed(() =>
    props.isOwner && !props.canWriteShoutouts
        ? 'A message for you'
        : `A message for ${props.profile.name}`
)

// ── Infinite scroll — auditions ───────────────────────────────────────────
const auditionsList    = ref([...(props.sponsoredAuditions.data ?? []).map(a => ({ ...a, _type: 'sponsored' }))])
const auditionsPage    = ref(props.sponsoredAuditions.current_page ?? 1)
const auditionsHasMore = ref(
    (props.sponsoredAuditions.current_page ?? 1) < (props.sponsoredAuditions.last_page ?? 1)
)
const auditionsLoading = ref(false)
const auditionsError   = ref(false)
const auditionsSentinel = ref(null)

const loadMoreAuditions = async () => {
    if (auditionsLoading.value || !auditionsHasMore.value) return
    auditionsLoading.value = true
    auditionsError.value = false
    try {
        const { data } = await axios.get(route(`${routePrefix.value}.profile.my.auditions`, { slug: props.profile.slug }), {
            params: { page: auditionsPage.value + 1 },
        })
        auditionsList.value.push(...(data.data ?? []).map(a => ({ ...a, _type: 'sponsored' })))
        auditionsPage.value = data.current_page
        auditionsHasMore.value = data.current_page < data.last_page
    } catch {
        auditionsError.value = true
    } finally {
        auditionsLoading.value = false
    }
}

// ── Infinite scroll — comments ────────────────────────────────────────────
const commentsList    = ref([...(props.comments.data ?? []).map(c => ({ ...c, _type: 'comment' }))])
const commentsPage    = ref(props.comments.current_page ?? 1)
const commentsHasMore = ref(
    (props.comments.current_page ?? 1) < (props.comments.last_page ?? 1)
)
const commentsLoading = ref(false)
const commentsError   = ref(false)
const commentsSentinel = ref(null)

// ── Combined activity feed ────────────────────────────────────────────────
const activityFeed = computed(() =>
    [...auditionsList.value, ...commentsList.value]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
)
const isActivityLoading = computed(() => auditionsLoading.value || commentsLoading.value)
const activityTotal     = computed(() => (props.sponsoredAuditions.total ?? 0) + (props.comments.total ?? 0))

const loadMoreComments = async () => {
    if (commentsLoading.value || !commentsHasMore.value) return
    commentsLoading.value = true
    commentsError.value = false
    try {
        const { data } = await axios.get(route(`${routePrefix.value}.profile.my.comments`, { slug: props.profile.slug }), {
            params: { page: commentsPage.value + 1 },
        })
        commentsList.value.push(...(data.data ?? []).map(c => ({ ...c, _type: 'comment' })))
        commentsPage.value = data.current_page
        commentsHasMore.value = data.current_page < data.last_page
    } catch {
        commentsError.value = true
    } finally {
        commentsLoading.value = false
    }
}

// ── IntersectionObserver wiring ───────────────────────────────────────────
let auditionsObserver = null
let commentsObserver  = null

// ── Guest CTA ─────────────────────────────────────────────────────────────
const heroRef        = ref(null)
const showGuestCta   = ref(false)
let   heroObserver   = null

onMounted(() => {
    document.body.classList.add('my-page-body')
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    // Lazy-fetch performer data when the backend skipped the API call
    if (props.performersLazy) {
        axios.get(route(`${routePrefix.value}.profile.my.performers`, { slug: props.profile.slug }))
            .then(({ data }) => {
                sponsoredPerformersLocal.value    = data.sponsoredPerformers    ?? []
                topPerformerAffinitiesLocal.value = data.topPerformerAffinities ?? []
                // Start carousel once data is available
                nextTick(() => {
                    const el = carouselEl.value
                    if (el && el.scrollWidth > el.clientWidth) {
                        carouselNeedsLoop.value = true
                        startCarousel()
                    }
                })
            })
            .catch(() => { /* non-critical, carousel/avatars stay empty */ })
            .finally(() => { isPerformersLoading.value = false })
    }

    if (sponsoredPerformersLocal.value.length > 1) {
        nextTick(() => {
            const el = carouselEl.value
            if (el && el.scrollWidth > el.clientWidth) {
                carouselNeedsLoop.value = true
                startCarousel()
            }
        })
    }
    if (props.shoutouts.length > 1) cycleShoutout()

    auditionsObserver = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) loadMoreAuditions() },
        { rootMargin: '200px' }
    )
    if (auditionsSentinel.value) auditionsObserver.observe(auditionsSentinel.value)

    commentsObserver = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) loadMoreComments() },
        { rootMargin: '200px' }
    )
    if (commentsSentinel.value) commentsObserver.observe(commentsSentinel.value)

    if (!isUserLoggedIn.value && heroRef.value) {
        heroObserver = new IntersectionObserver(
            ([entry]) => { showGuestCta.value = !entry.isIntersecting },
            { threshold: 0 }
        )
        heroObserver.observe(heroRef.value)
    }
})

onUnmounted(() => {
    document.body.classList.remove('my-page-body')
    if (carouselRaf) cancelAnimationFrame(carouselRaf)
    if (shoutoutTimer) clearInterval(shoutoutTimer)

    carouselObserver?.disconnect()
    auditionsObserver?.disconnect()
    commentsObserver?.disconnect()
    heroObserver?.disconnect()
})

// ── Shoutout form ──────────────────────────────────────────────────────────
const shoutoutModalOpen = ref(false)

const shoutoutForm = ref({
    creator_name: '',
    message:      '',
})
const shoutoutSaving  = ref(false)
const shoutoutSuccess = ref(false)
const shoutoutError   = ref(null)

const openShoutoutModal = () => {
    shoutoutForm.value  = { creator_name: '', message: '' }
    shoutoutSuccess.value = false
    shoutoutError.value   = null
    shoutoutModalOpen.value = true
}
const closeShoutoutModal = () => { shoutoutModalOpen.value = false }

const shoutoutPerformerOptions = computed(() =>
    sponsoredPerformersLocal.value.filter(p => p.name)
)

// O(1) avatar lookup — rebuilt only when sponsoredPerformersLocal changes
const performerAvatarMap = computed(() =>
    new Map(sponsoredPerformersLocal.value.filter(p => p.name).map(p => [p.name, p.avatar ?? null]))
)
const shoutoutAvatar = (creatorName) => performerAvatarMap.value.get(creatorName) ?? null

const submitShoutout = async () => {
    shoutoutSaving.value  = true
    shoutoutError.value   = null
    shoutoutSuccess.value = false
    try {
        await axios.post(
            route(routePrefix.value + '.profile.my.shoutout', { slug: props.profile.slug }),
            shoutoutForm.value,
        )
        shoutoutSuccess.value = true
        router.reload({ only: ['shoutouts'] })
        setTimeout(closeShoutoutModal, 800)
    } catch (e) {
        console.error(e);
        shoutoutError.value = e?.response?.data?.message ?? 'Failed to save shoutout.'
    } finally {
        shoutoutSaving.value = false
    }
}

// ── Helpers ────────────────────────────────────────────────────────────────
const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const performerInitial = (name) =>
    name ? name.charAt(0).toUpperCase() : '?'

const fundingPercent = (likes, goal) =>
    goal ? Math.min(100, Math.round((likes / goal) * 100)) : 0

const isFunded   = (a) => !!a.star_goal_reached_at
const isReleased = (a) => a.release_status === 2
const isInProd   = (a) => a.release_status === 1

// Pick the single most impressive audition to feature as the highlight card.
// Priority: released + #1 sponsor → released (any rank) → in-production + #1 → funded + #1
const highlightAudition = computed(() => {
    const all = props.sponsoredAuditions.data ?? []
    return (
        all.find(a => isReleased(a) && a.audition_rank === 1) ??
        all.find(a => isReleased(a)) ??
        all.find(a => isInProd(a)   && a.audition_rank === 1) ??
        all.find(a => isFunded(a)   && a.audition_rank === 1) ??
        null
    )
})

const releaseLabel = (a) => {
    if (isReleased(a)) return 'Released'
    if (isInProd(a))   return 'In Production'
    if (isFunded(a))   return 'Funded'
    return 'In Progress'
}

const releaseBadgeClass = (a) => {
    if (isReleased(a)) return 'bg-green-500/20 border-green-500/50 text-green-400'
    if (isInProd(a))   return 'bg-blue-500/20 border-blue-500/50 text-blue-400'
    if (isFunded(a))   return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
    return 'bg-white/5 border-white/10 text-white/40'
}

// Returns "Top X%" string for the given percentile, or null if not worth showing.
// percentile = % of users beaten (e.g. 95 → beaten 95% → Top 5%).
// Only surfaces tiers that feel like genuine bragging rights (top half or better).
const topPercentLabel = (percentile) => {
    if (!percentile || percentile <= 0) return null
    const top = 100 - percentile
    if (top <= 1)  return 'Top 1%'
    if (top <= 5)  return 'Top 5%'
    if (top <= 10) return 'Top 10%'
    if (top <= 25) return 'Top 25%'
    if (top <= 50) return 'Top 50%'
    return null
}

const formatNumber = (n) => {
    if (!n) return '0'
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
    return n.toString()
}

const navigateToAudition = (slug) => {
    trackFathomEvent('MyPage Audition Clicked')

    const routeName = routePrefix.value + '.reels.feed'

    const url = route(routeName, { tag: 'auditions', post: slug, sort: 'trending' })
    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}

// ── Achievement badges ────────────────────────────────────────────────────
const earnedBadges = computed(() => {
    const s = props.stats
    const badges = []

    // Stars given — tiered
    if (s.totalStarsGiven >= 2000) {
        badges.push({ id: 'star-legend',    label: 'Star Legend',    icon: Star,    classes: 'text-yellow-300 bg-yellow-500/15 border-yellow-500/40' })
    } else if (s.totalStarsGiven >= 500) {
        badges.push({ id: 'star-champion',  label: 'Star Champion',  icon: Star,    classes: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' })
    } else if (s.totalStarsGiven >= 100) {
        badges.push({ id: 'star-supporter', label: 'Star Supporter', icon: Star,    classes: 'text-amber-400  bg-amber-500/10  border-amber-500/25'  })
    } else if (s.totalStarsGiven >= 1) {
        badges.push({ id: 'first-star',     label: 'First Star',     icon: Star,    classes: 'text-orange-400 bg-orange-500/10 border-orange-400/25'  })
    }

    // Auditions sponsored — tiered
    if (s.auditionsSponsored >= 50) {
        badges.push({ id: 'super-sponsor', label: 'Super Sponsor',  icon: Zap,     classes: 'text-blue-300  bg-blue-400/10   border-blue-400/30'   })
    } else if (s.auditionsSponsored >= 10) {
        badges.push({ id: 'power-sponsor', label: 'Power Sponsor',  icon: Zap,     classes: 'text-blue-400  bg-blue-500/10   border-blue-500/25'   })
    } else if (s.auditionsSponsored >= 1) {
        badges.push({ id: 'first-backer',  label: 'First Backer',   icon: Zap,     classes: 'text-sky-400   bg-sky-500/10    border-sky-400/25'    })
    }

    // Producer credit — tiered
    if (s.releasedAuditionsCount >= 5) {
        badges.push({ id: 'exec-producer', label: 'Exec. Producer', icon: Film,    classes: 'text-green-300 bg-green-400/10  border-green-400/30'  })
    } else if (s.releasedAuditionsCount >= 1) {
        badges.push({ id: 'producer',      label: 'Producer',       icon: Film,    classes: 'text-green-400 bg-green-500/10  border-green-500/25'  })
    }

    // Leaderboard rank
    if (s.leaderboardRank > 0 && s.leaderboardRank <= 3) {
        badges.push({ id: 'elite',   label: 'Elite Sponsor', icon: Trophy, classes: 'text-yellow-300 bg-yellow-500/15 border-yellow-500/50' })
    } else if (s.leaderboardRank > 0 && s.leaderboardRank <= 10) {
        badges.push({ id: 'top-10',  label: 'Top 10',        icon: Trophy, classes: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' })
    } else if (s.leaderboardRank > 0 && s.leaderboardRank <= 100) {
        badges.push({ id: 'top-100', label: 'Top 100',       icon: Award,  classes: 'text-violet-300 bg-violet-500/10 border-violet-400/30'  })
    }

    return badges
})

// ── Badge progress ────────────────────────────────────────────────────────
// Returns one entry per stat category that hasn't reached its max tier yet.
// Each entry carries everything the template needs to render a progress bar.
const badgeProgress = computed(() => {
    const s = props.stats
    const items = []

    const progressEntry = (tiers, value, label, accentClass) => {
        let currentIdx = -1
        for (let i = tiers.length - 1; i >= 0; i--) {
            if (value >= tiers[i].threshold) { currentIdx = i; break }
        }
        if (currentIdx === tiers.length - 1) return null // already at max tier
        const from = currentIdx >= 0 ? tiers[currentIdx].threshold : 0
        const next = tiers[currentIdx + 1]
        const pct  = Math.max(0, Math.min(100, Math.round(((value - from) / (next.threshold - from)) * 100)))
        return {
            label,
            icon:        next.icon,
            currentTier: currentIdx >= 0 ? tiers[currentIdx] : null,
            nextTier:    next,
            current:     value,
            remaining:   next.threshold - value,
            pct,
            accentClass,
        }
    }

    const starTiers = [
        { threshold: 1,    label: 'First Star',     icon: Star },
        { threshold: 100,  label: 'Star Supporter',  icon: Star },
        { threshold: 500,  label: 'Star Champion',   icon: Star },
        { threshold: 2000, label: 'Star Legend',     icon: Star },
    ]
    const sponsorTiers = [
        { threshold: 1,  label: 'First Backer',  icon: Zap },
        { threshold: 10, label: 'Power Sponsor', icon: Zap },
        { threshold: 50, label: 'Super Sponsor', icon: Zap },
    ]
    const producerTiers = [
        { threshold: 1, label: 'Producer',       icon: Film },
        { threshold: 5, label: 'Exec. Producer', icon: Film },
    ]

    const stars    = progressEntry(starTiers,    s.totalStarsGiven,       'Stars Given',          'bg-yellow-500')
    const sponsored = progressEntry(sponsorTiers, s.auditionsSponsored,   'Auditions Sponsored',  'bg-blue-500')
    const producer  = progressEntry(producerTiers, s.releasedAuditionsCount, 'Released Auditions', 'bg-green-500')

    if (stars)    items.push(stars)
    if (sponsored) items.push(sponsored)
    if (producer)  items.push(producer)

    return items
})

// Visible role label shown in the hero — picks the single most impressive title
const profileRole = computed(() => {
    const s = props.stats
    if (s.leaderboardRank > 0 && s.leaderboardRank <= 3)   return `#${s.leaderboardRank} Top Sponsor`
    if (s.leaderboardRank > 0 && s.leaderboardRank <= 10)  return `Top 10 Sponsor`
    if (s.leaderboardRank > 0 && s.leaderboardRank <= 100) return `Top 100 Sponsor`
    if (s.releasedAuditionsCount >= 5)                     return `Executive Producer`
    if (s.releasedAuditionsCount >= 1)                     return `Producer`
    if (s.auditionsSponsored >= 50)                        return `Super Sponsor`
    if (s.auditionsSponsored >= 10)                        return `Power Sponsor`
    if (s.auditionsSponsored >= 1)                         return `Audition Sponsor`
    return null
})

// ── Share / Copy link ─────────────────────────────────────────────────────
const shareProfile = () => {
    trackFathomEvent('MyPage Share Clicked')
    document.getElementById('ShareLinkModal')?.classList.remove('hidden')
}

const milestoneConfig = {
    joined:        { icon: Zap,    color: 'text-blue-400',   ring: 'ring-blue-500/30',   bg: 'bg-blue-500/10'   },
    first_star:    { icon: Star,   color: 'text-yellow-400', ring: 'ring-yellow-500/30', bg: 'bg-yellow-500/10' },
    first_funded:  { icon: Zap,    color: 'text-blue-300',   ring: 'ring-blue-400/30',   bg: 'bg-blue-400/10'   },
    first_release: { icon: Film,   color: 'text-green-400',  ring: 'ring-green-500/30',  bg: 'bg-green-500/10'  },
    leaderboard:   { icon: Trophy, color: 'text-yellow-300', ring: 'ring-yellow-400/30', bg: 'bg-yellow-400/10' },
}

const formatDate = (iso) => {
    if (!iso) return null
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60)     return 'just now'
    if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`
    if (diff < 2592000)return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })
}

const handleJoinClick = (trigger) => {
    isStarPowerModalOpen.value = true
    trackFathomEvent(trigger || 'MyPage Join Clicked');
}

const goToAuditionsLanding = () => {
    trackFathomEvent('MyPage Auditions Landing Clicked')

    const url = getMainAuditionsUrl(isUserLoggedIn.value, routePrefix.value)
    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}

const handleBuyStarPack = (pack) => {
    if (pack.purchaseUrl) {
        const currentSearch = window.location.search
        const returnParams = new URLSearchParams(currentSearch)
        const returnUrl = window.location.pathname + '?' + returnParams.toString()
        const separator = pack.purchaseUrl.includes('?') ? '&' : '?'
        window.location.href = pack.purchaseUrl + separator + 'psc2=' + encodeURIComponent(returnUrl) + '&psc3=profile'
    }
}

</script>

<template>
    <div class="main-container">
        <div class="min-h-screen bg-black pb-24">

            <!-- ── HEADER ─────────────────────────────────────────── -->
            <HeaderV2
                :isUserLoggedIn="isUserLoggedIn"
                :isGoldUser="isGoldUser"
                :routePrefix="routePrefix"
                :requiresAgeVerification="false"
                :hide="{ videos: !isUserLoggedIn, getStars: !isUserLoggedIn, kebab: !isUserLoggedIn }"
                @joinClick="handleJoinClick"
                @goldJoinClick="isBuyStarsModalOpen = true"
            />

            <!-- ── HERO ───────────────────────────────────────────── -->
            <div ref="heroRef" class="relative bg-gradient-to-br from-[#00071a] via-[#000d2e] to-black">
                <!-- background layers -->
                <div class="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_100%,rgba(37,99,235,0.2),transparent)]"></div>
                <div class="absolute inset-0 opacity-[0.03]" style="background-image:radial-gradient(circle,#fff 1px,transparent 1px);background-size:24px 24px;"></div>

                <!-- all hero content flows naturally -->
                <div class="relative z-10 px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto pt-8 pb-6">

                    <!-- Avatar + identity row -->
                    <div class="flex items-center gap-4 md:gap-5 mb-6">
                        <!-- Avatar -->
                        <div class="w-20 h-20 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-blue-800 to-[#000d2e] border-4 border-black flex items-center justify-center shrink-0 shadow-xl ring-1 ring-white/10">
                            <span class="text-white text-2xl md:text-4xl font-bold select-none leading-none">
                                {{ profile.name?.charAt(0).toUpperCase() ?? '?' }}
                            </span>
                        </div>

                        <!-- Name + meta -->
                        <div class="min-w-0 flex-1">
                            <div class="flex items-start justify-between gap-3">
                                <div class="flex items-center gap-2 flex-wrap min-w-0">
                                    <h1 class="text-white text-xl md:text-2xl font-bold tracking-tight leading-tight">
                                        {{ profile.name }}
                                    </h1>
                                    <span
                                        v-if="stats.leaderboardRank > 0"
                                        class="flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0"
                                    >
                                        <Trophy class="w-3 h-3 shrink-0" />
                                        #{{ formatNumber(stats.leaderboardRank) }}
                                    </span>
                                </div>

                            </div>
                            <div class="flex items-center gap-2 mt-1 flex-wrap">
                                <span v-if="profileRole" class="text-blue-400 text-xs font-semibold tracking-wide">
                                    {{ profileRole }}
                                </span>
                                <span v-if="false && profileRole" class="text-white/20 text-xs">·</span>
                                <p v-if="false" class="text-white/35 text-xs tracking-wide uppercase">
                                    Member since {{ profile.memberSince }}
                                </p>
                            </div>

                            <!-- Achievement badges -->
                            <div v-if="earnedBadges.length" class="flex flex-wrap gap-1.5 mt-3">
                                <span
                                    v-for="badge in earnedBadges"
                                    :key="badge.id"
                                    :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold', badge.classes]"
                                >
                                    <component :is="badge.icon" class="w-3 h-3 shrink-0" />
                                    {{ badge.label }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Share strip -->
                    <button
                        @click="shareProfile"
                        class="mt-6 w-full flex items-center justify-between gap-3 rounded-2xl bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/20 hover:border-blue-500/40 px-4 py-3 transition-all duration-200 group"
                    >
                        <div class="text-left min-w-0">
                            <p class="text-white/80 text-sm font-semibold group-hover:text-white transition-colors">
                                {{ props.isOwner ? 'Your stats are worth showing off' : `Know ${profile.name}? Share their profile` }}
                            </p>
                            <p class="text-white/35 text-xs mt-0.5">
                                {{ props.isOwner ? 'Share your profile and let people see your impact' : 'Help spread the word and show some support' }}
                            </p>
                        </div>
                        <div class="flex items-center gap-2 shrink-0 bg-blue-600 group-hover:bg-blue-500 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-all shadow-md shadow-blue-600/30">
                            <Share2 class="w-4 h-4" />
                            <span>Share</span>
                        </div>
                    </button>

                    <!-- stats grid — inside the banner, flows naturally below identity -->
                    <div class="grid grid-cols-3 sm:grid-cols-6 divide-x divide-white/10 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden mt-3">

                        <!-- Stars Given -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5">
                            <div class="w-7 h-7 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
                                <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(stats.totalStarsGiven) }}</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Stars Given</span>
                            <span v-if="topPercentLabel(stats.starsPercentile)" class="text-yellow-400 text-[10px] font-bold uppercase tracking-wider">{{ topPercentLabel(stats.starsPercentile) }}</span>
                        </div>

                        <!-- Auditions Sponsored -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5">
                            <div class="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                <Film class="text-blue-400 w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(stats.auditionsSponsored) }}</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Sponsored</span>
                            <span v-if="topPercentLabel(stats.sponsoredPercentile)" class="text-blue-400 text-[10px] font-bold uppercase tracking-wider">{{ topPercentLabel(stats.sponsoredPercentile) }}</span>
                        </div>

                        <!-- Comments -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5">
                            <div class="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                <MessageCircle class="text-blue-400 w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(stats.commentsMade) }}</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Comments</span>
                        </div>

                        <!-- Leaderboard rank -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5 border-t sm:border-t-0 border-white/10">
                            <div class="w-7 h-7 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
                                <Trophy class="w-3.5 h-3.5 shrink-0 text-yellow-400" />
                            </div>
                            <span v-if="stats.leaderboardRank > 0" class="text-yellow-400 text-xl md:text-2xl font-bold tabular-nums leading-none">#{{ formatNumber(stats.leaderboardRank) }}</span>
                            <span v-else class="text-white/20 text-xl md:text-2xl font-bold leading-none">—</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Leaderboard</span>
                            <span v-if="topPercentLabel(stats.rankPercentile)" class="text-yellow-400 text-[10px] font-bold uppercase tracking-wider">{{ topPercentLabel(stats.rankPercentile) }}</span>
                        </div>

                        <!-- Casting Requests -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5 border-t sm:border-t-0 border-white/10">
                            <div class="w-7 h-7 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                                <Clapperboard class="text-purple-400 w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(requestedCastings.filter(c => !c.deleted).length) }}</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Requested</span>
                        </div>

                        <!-- Castings Supported -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5 border-t sm:border-t-0 border-white/10">
                            <div class="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                                <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5 opacity-80" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(supportedCastings.length) }}</span>
                            <span class="text-white/35 text-[10px] uppercase tracking-wider">Cast. Backed</span>
                        </div>
                    </div>

                </div>
            </div>

            <!-- ── PERFORMER SHOUTOUT SPOTLIGHT ───────────────────── -->
            <section v-if="shoutouts.length && activeShoutout" class="relative overflow-hidden">
                <!-- full-bleed background -->
                <div class="absolute inset-0 bg-gradient-to-b from-[#0a0800] via-[#100d00] to-black"></div>
                <div class="absolute inset-0 bg-[radial-gradient(ellipse_80%_100%_at_50%_0%,rgba(200,168,75,0.13),transparent)]"></div>
                <!-- top and bottom bleed seams -->
                <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent"></div>
                <div class="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-yellow-500/10 to-transparent"></div>
                <!-- shimmer sweep across full width -->
                <div class="absolute inset-x-0 top-0 h-40 pointer-events-none overflow-hidden">
                    <div class="shimmer-sweep absolute inset-0"></div>
                </div>
                <!-- giant decorative quote -->
                <div class="absolute top-4 left-1/2 -translate-x-1/2 text-[16rem] leading-none font-serif text-yellow-500/[0.04] select-none pointer-events-none" aria-hidden="true">"</div>

                <Transition name="shoutout-fade" mode="out-in">
                    <div :key="shoutoutIndex" class="relative z-10 py-14 px-4 md:px-8 lg:px-16 max-w-3xl mx-auto flex flex-col items-center text-center">

                        <!-- eyebrow -->
                        <p class="text-yellow-500/60 text-[11px] font-semibold uppercase tracking-[0.2em] mb-8">{{ shoutoutEyebrow }}</p>

                        <!-- avatar -->
                        <div class="relative mb-6">
                            <div class="absolute inset-0 rounded-full blur-xl bg-yellow-500/30 scale-150"></div>
                            <div class="relative w-20 h-20 rounded-full ring-2 ring-yellow-400/50 overflow-hidden bg-gradient-to-br from-yellow-800/40 to-yellow-600/10 flex items-center justify-center shadow-2xl">
                                <img
                                    v-if="shoutoutAvatar(activeShoutout.creator_name)"
                                    :src="shoutoutAvatar(activeShoutout.creator_name)"
                                    :alt="activeShoutout.creator_name"
                                    class="w-full h-full object-cover"
                                />
                                <span v-else class="text-yellow-300 text-2xl font-bold select-none">{{ performerInitial(activeShoutout.creator_name) }}</span>
                            </div>
                        </div>

                        <!-- message -->
                        <p class="shoutout-message text-white/90 text-xl md:text-2xl lg:text-3xl font-light italic leading-relaxed mb-8 max-w-2xl">
                            "{{ activeShoutout.message }}"
                        </p>

                        <!-- signature -->
                        <div class="flex items-center gap-4">
                            <div class="h-px w-10 bg-yellow-500/30"></div>
                            <span class="text-yellow-400 text-sm font-semibold tracking-widest uppercase">{{ activeShoutout.creator_name }}</span>
                            <div class="h-px w-10 bg-yellow-500/30"></div>
                        </div>

                        <!-- dots indicator for multiple shoutouts -->
                        <div v-if="shoutouts.length > 1" class="flex items-center gap-2 mt-8">
                            <button
                                v-for="(_, i) in shoutouts"
                                :key="i"
                                @click="shoutoutIndex = i"
                                :class="['w-1.5 h-1.5 rounded-full transition-all duration-300', i === shoutoutIndex ? 'bg-yellow-400 w-4' : 'bg-white/20 hover:bg-white/40']"
                            />
                        </div>
                    </div>
                </Transition>
            </section>

            <!-- ── TOP 3 BIGGEST SUPPORTERS ──────────────────────── -->
            <!-- skeleton while performers are loading -->
            <div
                v-if="isPerformersLoading"
                class="px-4 md:px-8 lg:px-16 mt-8 max-w-[1400px] mx-auto"
            >
                <div class="h-3 w-32 rounded-full bg-white/5 animate-pulse mx-auto mb-4"></div>
                <div class="flex flex-col sm:flex-row items-stretch gap-3 justify-center">
                    <!-- #1 gold card skeleton -->
                    <div class="relative flex-1 sm:order-2 overflow-hidden rounded-2xl border border-yellow-500/10 bg-yellow-500/[0.03] animate-pulse">
                        <div class="flex flex-col items-center text-center px-5 py-6 gap-3">
                            <div class="w-16 h-16 rounded-full bg-white/5"></div>
                            <div class="h-3 w-20 rounded-full bg-white/5"></div>
                            <div class="h-8 w-10 rounded-md bg-white/5"></div>
                            <div class="h-2.5 w-14 rounded-full bg-white/5"></div>
                        </div>
                    </div>
                    <!-- #2 silver card skeleton -->
                    <div class="relative flex-1 sm:order-1 overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] animate-pulse">
                        <div class="flex flex-col items-center text-center px-5 py-5 gap-3">
                            <div class="w-[52px] h-[52px] rounded-full bg-white/5"></div>
                            <div class="h-2.5 w-16 rounded-full bg-white/5"></div>
                            <div class="h-7 w-8 rounded-md bg-white/5"></div>
                            <div class="h-2 w-12 rounded-full bg-white/5"></div>
                        </div>
                    </div>
                    <!-- #3 bronze card skeleton -->
                    <div class="relative flex-1 sm:order-3 overflow-hidden rounded-2xl border border-orange-500/5 bg-orange-500/[0.02] animate-pulse">
                        <div class="flex flex-col items-center text-center px-5 py-5 gap-3">
                            <div class="w-[52px] h-[52px] rounded-full bg-white/5"></div>
                            <div class="h-2.5 w-16 rounded-full bg-white/5"></div>
                            <div class="h-7 w-8 rounded-md bg-white/5"></div>
                            <div class="h-2 w-12 rounded-full bg-white/5"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-else-if="topPerformerAffinitiesLocal.length"
                class="px-4 md:px-8 lg:px-16 mt-8 max-w-[1400px] mx-auto"
            >
                <p class="text-white/25 text-[11px] font-semibold uppercase tracking-widest mb-4 text-center">
                    Biggest Supporters
                </p>

                <div class="flex flex-col sm:flex-row items-stretch gap-3 justify-center">
                    <!-- Rank #1 — tallest card with gold treatment -->
                    <div
                        v-if="topPerformerAffinitiesLocal[0]"
                        class="relative flex-1 sm:order-2 overflow-hidden rounded-2xl border border-yellow-500/30 bg-gradient-to-b from-yellow-500/[0.07] to-transparent"
                    >
                        <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/60 to-transparent"></div>
                        <div class="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,rgba(234,179,8,0.06),transparent)] pointer-events-none"></div>

                        <!-- rank badge -->
                        <div class="absolute top-3 right-3 w-6 h-6 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center">
                            <span class="text-yellow-400 text-[10px] font-bold leading-none">1</span>
                        </div>

                        <div class="relative flex flex-col items-center text-center px-5 py-6 gap-3">
                            <!-- avatar -->
                            <div class="w-16 h-16 rounded-full ring-2 ring-yellow-400/50 overflow-hidden bg-yellow-500/10 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                                <img
                                    v-if="topPerformerAffinitiesLocal[0].avatar"
                                    :src="topPerformerAffinitiesLocal[0].avatar"
                                    :alt="topPerformerAffinitiesLocal[0].name"
                                    class="w-full h-full object-cover"
                                />
                                <span v-else class="text-yellow-300 text-xl font-bold select-none leading-none">
                                    {{ topPerformerAffinitiesLocal[0].name.charAt(0).toUpperCase() }}
                                </span>
                            </div>

                            <!-- name + count -->
                            <div>
                                <p class="text-white font-bold text-base leading-tight">{{ topPerformerAffinitiesLocal[0].name }}</p>
                                <p class="text-yellow-400 text-3xl font-bold tabular-nums leading-none mt-2">{{ topPerformerAffinitiesLocal[0].count }}</p>
                                <p class="text-white/35 text-[11px] uppercase tracking-wider mt-1">{{ topPerformerAffinitiesLocal[0].count === 1 ? 'Audition' : 'Auditions' }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Rank #2 — silver -->
                    <div
                        v-if="topPerformerAffinitiesLocal[1]"
                        class="relative flex-1 sm:order-1 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent"
                    >
                        <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                        <!-- rank badge -->
                        <div class="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                            <span class="text-white/50 text-[10px] font-bold leading-none">2</span>
                        </div>

                        <div class="relative flex flex-col items-center text-center px-5 py-5 gap-3">
                            <!-- avatar -->
                            <div class="w-13 h-13 w-[52px] h-[52px] rounded-full ring-1 ring-white/20 overflow-hidden bg-white/5 flex items-center justify-center">
                                <img
                                    v-if="topPerformerAffinitiesLocal[1].avatar"
                                    :src="topPerformerAffinitiesLocal[1].avatar"
                                    :alt="topPerformerAffinitiesLocal[1].name"
                                    class="w-full h-full object-cover"
                                />
                                <span v-else class="text-white/40 text-lg font-bold select-none leading-none">
                                    {{ topPerformerAffinitiesLocal[1].name.charAt(0).toUpperCase() }}
                                </span>
                            </div>

                            <!-- name + count -->
                            <div>
                                <p class="text-white/80 font-semibold text-sm leading-tight">{{ topPerformerAffinitiesLocal[1].name }}</p>
                                <p class="text-white/60 text-2xl font-bold tabular-nums leading-none mt-2">{{ topPerformerAffinitiesLocal[1].count }}</p>
                                <p class="text-white/25 text-[11px] uppercase tracking-wider mt-1">{{ topPerformerAffinitiesLocal[1].count === 1 ? 'Audition' : 'Auditions' }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Rank #3 — bronze -->
                    <div
                        v-if="topPerformerAffinitiesLocal[2]"
                        class="relative flex-1 sm:order-3 overflow-hidden rounded-2xl border border-orange-500/15 bg-gradient-to-b from-orange-500/[0.04] to-transparent"
                    >
                        <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent"></div>

                        <!-- rank badge -->
                        <div class="absolute top-3 right-3 w-6 h-6 rounded-full bg-orange-500/10 border border-orange-500/25 flex items-center justify-center">
                            <span class="text-orange-400/70 text-[10px] font-bold leading-none">3</span>
                        </div>

                        <div class="relative flex flex-col items-center text-center px-5 py-5 gap-3">
                            <!-- avatar -->
                            <div class="w-[52px] h-[52px] rounded-full ring-1 ring-orange-500/20 overflow-hidden bg-orange-500/5 flex items-center justify-center">
                                <img
                                    v-if="topPerformerAffinitiesLocal[2].avatar"
                                    :src="topPerformerAffinitiesLocal[2].avatar"
                                    :alt="topPerformerAffinitiesLocal[2].name"
                                    class="w-full h-full object-cover"
                                />
                                <span v-else class="text-orange-400/50 text-lg font-bold select-none leading-none">
                                    {{ topPerformerAffinitiesLocal[2].name.charAt(0).toUpperCase() }}
                                </span>
                            </div>

                            <!-- name + count -->
                            <div>
                                <p class="text-white/70 font-semibold text-sm leading-tight">{{ topPerformerAffinitiesLocal[2].name }}</p>
                                <p class="text-orange-400/80 text-2xl font-bold tabular-nums leading-none mt-2">{{ topPerformerAffinitiesLocal[2].count }}</p>
                                <p class="text-white/25 text-[11px] uppercase tracking-wider mt-1">{{ topPerformerAffinitiesLocal[2].count === 1 ? 'Audition' : 'Auditions' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── PRODUCER CREDIT ────────────────────────────────── -->
            <div
                v-if="stats.releasedAuditionsCount > 0"
                class="px-4 md:px-8 lg:px-16 mt-6 max-w-[1400px] mx-auto"
            >
                <div class="relative overflow-hidden flex items-center gap-5 px-6 py-5 rounded-2xl bg-gradient-to-r from-yellow-500/[0.09] via-yellow-500/[0.04] to-transparent border border-yellow-500/25">
                    <!-- subtle shimmer line at top -->
                    <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent"></div>

                    <div class="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center shrink-0">
                        <img :src="STAR_ICON_URL" alt="star" class="w-6 h-6" />
                    </div>

                    <div class="min-w-0">
                        <p class="text-yellow-300 text-sm font-bold leading-tight tracking-wide uppercase">
                            Producer Credit
                        </p>
                        <p class="text-white/60 text-sm mt-1 leading-relaxed">
                            <span class="text-white font-semibold">{{ profile.name }}</span>
                            helped bring
                            <span class="text-yellow-400 font-bold">{{ stats.releasedAuditionsCount }}</span>
                            {{ stats.releasedAuditionsCount === 1 ? 'audition' : 'auditions' }} to life as an official release{{ stats.releasedAuditionsCount === 1 ? '' : 's' }}.
                        </p>
                    </div>

                    <div class="ml-auto shrink-0 hidden sm:flex flex-col items-center gap-0.5">
                        <span class="text-yellow-400 text-3xl font-bold tabular-nums leading-none">{{ stats.releasedAuditionsCount }}</span>
                        <span class="text-yellow-500/60 text-[10px] uppercase tracking-wider">{{ stats.releasedAuditionsCount === 1 ? 'Release' : 'Releases' }}</span>
                    </div>
                </div>
            </div>

            <!-- ── YOU MADE THIS HAPPEN ───────────────────────────── -->
            <div
                v-if="highlightAudition"
                class="px-4 md:px-8 lg:px-16 mt-6 max-w-[1400px] mx-auto"
            >
                <div class="relative overflow-hidden rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-950/60 via-[#0d0900]/80 to-black">
                    <!-- shimmer line top -->
                    <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/60 to-transparent"></div>
                    <!-- background glow -->
                    <div class="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_0%_50%,rgba(234,179,8,0.07),transparent)] pointer-events-none"></div>

                    <div class="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 px-6 py-6">
                        <!-- Star icon -->
                        <div class="shrink-0 w-14 h-14 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center shadow-lg shadow-yellow-500/10">
                            <img :src="STAR_ICON_URL" alt="star" class="w-8 h-8" />
                        </div>

                        <!-- Text -->
                        <div class="flex-1 min-w-0">
                            <p class="text-yellow-400/70 text-[11px] font-bold uppercase tracking-widest mb-1">
                                {{ isOwner && !canWriteShoutouts ? 'You Made This Happen' : `${profile.name} Made This Happen` }}
                            </p>
                            <h2 class="text-white text-base md:text-lg font-bold leading-snug line-clamp-2">
                                {{ highlightAudition.title }}
                            </h2>
                            <p class="text-yellow-400/60 text-xs mt-0.5 truncate">{{ highlightAudition.creator_name }}</p>

                            <!-- meta row -->
                            <div class="flex flex-wrap items-center gap-2 mt-3">
                                <!-- rank badge -->
                                <span
                                    v-if="highlightAudition.audition_rank === 1"
                                    class="inline-flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-[11px] font-bold px-2.5 py-1 rounded-full"
                                >
                                    <Trophy class="w-3 h-3 shrink-0" /> Top Sponsor
                                </span>
                                <span
                                    v-else-if="highlightAudition.audition_rank"
                                    class="inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white/50 text-[11px] font-bold px-2.5 py-1 rounded-full"
                                >
                                    <Trophy class="w-3 h-3 shrink-0" /> #{{ highlightAudition.audition_rank }} Sponsor
                                </span>

                                <!-- release status -->
                                <span :class="['text-[11px] font-semibold px-2.5 py-1 rounded-full border uppercase tracking-wide', releaseBadgeClass(highlightAudition)]">
                                    {{ releaseLabel(highlightAudition) }}
                                </span>

                                <!-- stars contributed -->
                                <span class="inline-flex items-center gap-1 text-white/40 text-[11px]">
                                    <img :src="STAR_ICON_URL" alt="star" class="w-3 h-3" />
                                    {{ formatNumber(highlightAudition.stars_given) }} stars given
                                </span>

                                <!-- contribution % -->
                                <span
                                    v-if="highlightAudition.star_goal"
                                    class="text-yellow-400/60 text-[11px] font-semibold"
                                >
                                    ({{ fundingPercent(highlightAudition.stars_given, highlightAudition.star_goal) }}% of goal)
                                </span>
                            </div>
                        </div>

                        <!-- Watch Scene CTA -->
                        <a
                            v-if="highlightAudition.scene_slug"
                            :href="route(routePrefix + '.scene.show', highlightAudition.scene_slug)"
                            class="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 hover:border-yellow-500/50 text-yellow-400 hover:text-yellow-300 text-xs font-bold uppercase tracking-wide transition-all duration-150 whitespace-nowrap"
                        >
                            <Film class="w-3.5 h-3.5 shrink-0" />
                            Watch Scene
                        </a>
                        <a
                            v-else
                            :href="route(routePrefix + '.reels.feed', { tag: 'auditions', post: highlightAudition.audition_slug, sort: 'trending' })"
                            class="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/60 hover:text-white text-xs font-bold uppercase tracking-wide transition-all duration-150 whitespace-nowrap"
                        >
                            <Film class="w-3.5 h-3.5 shrink-0" />
                            View Audition
                        </a>
                    </div>
                </div>
            </div>


            <!-- ── MILESTONE TIMELINE ────────────────────────────── -->
            <div
                v-if="false && milestones.length > 1"
                class="px-4 md:px-8 lg:px-16 mt-6 max-w-[1400px] mx-auto"
            >
                <p class="text-white/25 text-[11px] font-semibold uppercase tracking-widest mb-4">Journey</p>

                <div class="relative">
                    <!-- vertical connector line -->
                    <div class="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-white/10 via-white/5 to-transparent"></div>

                    <div class="space-y-1">
                        <div
                            v-for="(milestone, idx) in milestones"
                            :key="idx"
                            class="relative flex items-start gap-4 py-2"
                        >
                            <!-- icon node -->
                            <div
                                :class="[
                                    'relative z-10 shrink-0 w-10 h-10 rounded-full ring-1 flex items-center justify-center',
                                    milestoneConfig[milestone.type]?.bg ?? 'bg-white/5',
                                    milestoneConfig[milestone.type]?.ring ?? 'ring-white/10',
                                ]"
                            >
                                <component
                                    :is="milestoneConfig[milestone.type]?.icon ?? Star"
                                    :class="['w-4 h-4 shrink-0', milestoneConfig[milestone.type]?.color ?? 'text-white/40']"
                                />
                            </div>

                            <!-- content -->
                            <div class="flex-1 min-w-0 pt-1.5">
                                <div class="flex items-baseline justify-between gap-3">
                                    <p class="text-white/80 text-sm font-semibold leading-tight">
                                        {{ milestone.label }}
                                    </p>
                                    <span class="shrink-0 text-white/25 text-[11px] tabular-nums">
                                        {{ milestone.date ? formatDate(milestone.date) : 'Current' }}
                                    </span>
                                </div>
                                <p v-if="milestone.detail" class="text-white/35 text-xs mt-0.5 line-clamp-1">
                                    {{ milestone.detail }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── BIO ────────────────────────────────────────────── -->
            <div class="px-4 md:px-8 lg:px-16 mt-6 max-w-[1400px] mx-auto">

                <p v-if="profile.bio" class="text-white/50 text-sm md:text-base leading-relaxed max-w-2xl">{{ profile.bio }}</p>

                <!-- badge progress — only rendered when at least one tier is incomplete -->
                <div v-if="false && badgeProgress.length" class="space-y-2">
                    <div
                        v-for="item in badgeProgress"
                        :key="item.label"
                        class="flex items-center gap-4 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3"
                    >
                        <!-- icon -->
                        <div class="shrink-0 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                            <component :is="item.icon" class="w-4 h-4 text-white/40" />
                        </div>

                        <!-- label + bar -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center justify-between mb-1.5">
                                <span class="text-white/40 text-[11px] uppercase tracking-wider">{{ item.label }}</span>
                                <span class="text-white/30 text-[11px] tabular-nums shrink-0">
                                    {{ item.current }} / {{ item.nextTier.threshold }}
                                </span>
                            </div>
                            <div class="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                <div
                                    class="h-full rounded-full transition-all duration-700"
                                    :class="item.accentClass"
                                    :style="{ width: item.pct + '%' }"
                                ></div>
                            </div>
                        </div>

                        <!-- next tier callout -->
                        <div class="shrink-0 text-right hidden sm:block">
                            <p class="text-white/25 text-[11px] leading-tight">
                                <span class="text-white/50 font-semibold tabular-nums">{{ item.remaining }}</span>
                                more to unlock
                            </p>
                            <p class="text-white/40 text-[11px] font-semibold mt-0.5">{{ item.nextTier.label }}</p>
                        </div>
                    </div>
                </div>

            </div>
            <!-- ── SPONSORED PERFORMERS CAROUSEL ─────────────────── -->
            <!-- skeleton while performers are loading -->
            <section
                v-if="isPerformersLoading"
                class="mt-12 max-w-[1400px] mx-auto animate-pulse"
            >
                <div class="flex items-center gap-3 mb-5 px-4 md:px-8 lg:px-16">
                    <div class="h-6 w-44 rounded-md bg-white/5"></div>
                    <div class="h-3 w-16 rounded-full bg-white/5"></div>
                </div>
                <div class="flex gap-5 overflow-hidden px-4 md:px-8 lg:px-16 pb-3">
                    <div
                        v-for="i in 8"
                        :key="i"
                        class="shrink-0 flex flex-col items-center gap-3 w-20 md:w-24"
                    >
                        <div class="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/5"></div>
                        <div class="h-2.5 w-14 rounded-full bg-white/5"></div>
                    </div>
                </div>
            </section>

            <section
                v-else-if="sponsoredPerformersLocal.length"
                class="mt-12 max-w-[1400px] mx-auto"
            >
                <div class="flex items-center gap-3 mb-5 px-4 md:px-8 lg:px-16">
                    <span class="rounded bg-[#141414]/20 border border-blue-600 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                        Performers Sponsored
                    </span>
                    <span class="text-white/30 text-xs">{{ sponsoredPerformersLocal.length }} performer{{ sponsoredPerformersLocal.length !== 1 ? 's' : '' }}</span>
                </div>

                <!-- scroll wrapper with gradient fade edges -->
                <div class="relative"
                    @mouseenter="carouselPaused = true"
                    @mouseleave="carouselPaused = false"
                    @touchstart.passive="carouselPaused = true"
                    @touchend.passive="carouselPaused = false"
                >
                    <!-- left fade -->
                    <div class="pointer-events-none absolute left-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-r from-black to-transparent z-10"></div>
                    <!-- right fade -->
                    <div class="pointer-events-none absolute right-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-l from-black to-transparent z-10"></div>

                    <!--
                        The list is rendered twice back-to-back so the RAF ticker
                        can jump silently from the end of copy-1 back to the start
                        of copy-1, making the loop feel endless.
                    -->
                    <div
                        ref="carouselEl"
                        class="flex gap-5 overflow-x-auto px-4 md:px-8 lg:px-16 pb-3 performers-scroll"
                        style="cursor: default; user-select: none;"
                    >
                        <!-- copy 1 -->
                        <div
                            v-for="(performer, i) in sponsoredPerformersLocal"
                            :key="'a-' + i"
                            class="shrink-0 flex flex-col items-center gap-3 w-20 md:w-24"
                            aria-hidden="false"
                        >
                            <div class="group relative w-16 h-16 md:w-20 md:h-20 rounded-full ring-2 ring-white/10 overflow-hidden bg-gradient-to-br from-[#00081f] to-[#000d2e] flex items-center justify-center hover:ring-blue-600/60 transition-all duration-200">
                                <img
                                    v-if="performer.avatar"
                                    :src="performer.avatar"
                                    :alt="performer.name"
                                    class="w-full h-full object-cover"
                                    loading="lazy"
                                    draggable="false"
                                />
                                <span v-else class="text-white/60 text-xl md:text-2xl font-bold select-none">
                                    {{ performerInitial(performer.name) }}
                                </span>
                            </div>
                            <p class="text-white/60 text-[11px] md:text-xs text-center leading-tight line-clamp-2 w-full">
                                {{ performer.name }}
                            </p>
                        </div>

                        <!-- copy 2 — seamless loop duplicate, only rendered when copy 1 overflows -->
                        <div
                            v-if="carouselNeedsLoop"
                            v-for="(performer, i) in sponsoredPerformersLocal"
                            :key="'b-' + i"
                            class="shrink-0 flex flex-col items-center gap-3 w-20 md:w-24"
                            aria-hidden="true"
                        >
                            <div class="group relative w-16 h-16 md:w-20 md:h-20 rounded-full ring-2 ring-white/10 overflow-hidden bg-gradient-to-br from-[#00081f] to-[#000d2e] flex items-center justify-center hover:ring-blue-600/60 transition-all duration-200">
                                <img
                                    v-if="performer.avatar"
                                    :src="performer.avatar"
                                    :alt="performer.name"
                                    class="w-full h-full object-cover"
                                    loading="lazy"
                                    draggable="false"
                                />
                                <span v-else class="text-white/60 text-xl md:text-2xl font-bold select-none">
                                    {{ performerInitial(performer.name) }}
                                </span>
                            </div>
                            <p class="text-white/60 text-[11px] md:text-xs text-center leading-tight line-clamp-2 w-full">
                                {{ performer.name }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ── SHOUTOUT FAB (admin only) ──────────────────────── -->
            <Teleport to="body">
                <!-- Floating bubble button -->
                <button
                    v-if="canWriteShoutouts"
                    @click="openShoutoutModal"
                    class="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-yellow-500 hover:bg-yellow-400 shadow-lg shadow-yellow-900/40 flex items-center justify-center transition-all duration-200 hover:scale-110"
                    title="Write a performer shoutout"
                >
                    <MessageSquarePlus class="w-6 h-6 text-black" />
                </button>

                <!-- Modal -->
                <Transition name="modal">
                    <div
                        v-if="shoutoutModalOpen"
                        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/70 backdrop-blur-sm"
                        @click.self="closeShoutoutModal"
                    >
                        <div class="relative w-full max-w-lg bg-[#0e0e12] border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">

                            <!-- header -->
                            <div class="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/8">
                                <p class="text-white/80 text-sm font-semibold">Write a Performer Shoutout</p>
                                <button @click="closeShoutoutModal" class="text-white/30 hover:text-white/70 transition-colors">
                                    <X class="w-5 h-5" />
                                </button>
                            </div>

                            <!-- form body -->
                            <div class="p-6 space-y-5">
                                <!-- performer picker -->
                                <div>
                                    <label class="text-white/40 text-xs mb-1.5 block">Performer</label>
                                    <select
                                        v-model="shoutoutForm.creator_name"
                                        class="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-yellow-500/50"
                                    >
                                        <option value="" disabled>Select a performer…</option>
                                        <option
                                            v-for="p in shoutoutPerformerOptions"
                                            :key="p.name"
                                            :value="p.name"
                                        >{{ p.name }}</option>
                                    </select>
                                </div>

                                <!-- message -->
                                <div>
                                    <label class="text-white/40 text-xs mb-1.5 block">Message</label>
                                    <textarea
                                        v-model="shoutoutForm.message"
                                        rows="5"
                                        maxlength="1000"
                                        placeholder="Write something from the performer…"
                                        class="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 resize-none focus:outline-none focus:border-yellow-500/50"
                                    ></textarea>
                                    <p class="text-white/20 text-xs text-right mt-1">{{ shoutoutForm.message.length }} / 1000</p>
                                </div>

                                <!-- feedback -->
                                <p v-if="shoutoutSuccess" class="text-green-400 text-sm">Shoutout saved successfully.</p>
                                <p v-if="shoutoutError"   class="text-red-400 text-sm">{{ shoutoutError }}</p>
                            </div>

                            <!-- footer -->
                            <div class="px-6 pb-6 flex items-center justify-end gap-3">
                                <button
                                    @click="closeShoutoutModal"
                                    class="px-4 py-2 rounded-lg text-white/40 hover:text-white/70 text-sm transition-colors"
                                >Cancel</button>
                                <button
                                    @click="submitShoutout"
                                    :disabled="shoutoutSaving || !shoutoutForm.creator_name || !shoutoutForm.message.trim()"
                                    class="px-5 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-bold transition-colors"
                                >
                                    {{ shoutoutSaving ? 'Saving…' : 'Save Shoutout' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition>
            </Teleport>

            <!-- ── MAIN + SIDEBAR LAYOUT ──────────────────────────── -->
            <div class="mt-14 max-w-[1400px] mx-auto px-4 md:px-8 lg:px-16 flex items-start gap-10">

            <!-- ── MAIN COLUMN ────────────────────────────────────── -->
            <div class="flex-1 min-w-0">

            <!-- ── ACTIVITY FEED ───────────────────────────────────── -->
            <section>
                <div class="flex items-center gap-3 mb-6">
                    <span class="rounded bg-[#141414]/20 border border-blue-600 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                        Activity
                    </span>
                    <span class="text-white/30 text-xs">{{ activityTotal }} total</span>
                </div>

                <!-- feed items -->
                <div v-if="activityFeed.length" class="space-y-3">
                    <template v-for="item in activityFeed" :key="item._type + '-' + item.id">

                        <!-- ── Sponsored audition ── -->
                        <div
                            v-if="item._type === 'sponsored'"
                            role="link"
                            tabindex="0"
                            @click="navigateToAudition(item.audition_slug)"
                            @keydown.enter="navigateToAudition(item.audition_slug)"
                            :class="[
                                'group relative bg-[#0e0e0e] rounded-xl overflow-hidden transition-all duration-300 cursor-pointer',
                                item.audition_rank === 1
                                    ? 'border border-yellow-500/50 top-sponsor-card'
                                    : 'border border-white/5 hover:border-blue-600/40 hover:shadow-[0_0_20px_rgba(37,99,235,0.08)]',
                            ]"
                        >
                            <!-- shimmer for top sponsor -->
                            <div v-if="item.audition_rank === 1" class="absolute inset-x-0 top-0 h-24 pointer-events-none overflow-hidden z-10">
                                <div class="shimmer-sweep absolute inset-0"></div>
                            </div>

                            <div class="flex items-start gap-4 p-4">
                                <!-- performer avatar -->
                                <div class="shrink-0 w-8 h-8 mt-0.5 rounded-full overflow-hidden border flex items-center justify-center"
                                    :class="item.audition_rank === 1 ? 'border-yellow-500/40 bg-yellow-900/20' : 'border-white/10 bg-white/5'">
                                    <img
                                        v-if="shoutoutAvatar(item.creator_name)"
                                        :src="shoutoutAvatar(item.creator_name)"
                                        :alt="item.creator_name"
                                        class="w-full h-full object-cover"
                                    />
                                    <span v-else
                                        class="text-[11px] font-bold select-none leading-none"
                                        :class="item.audition_rank === 1 ? 'text-yellow-400' : 'text-white/50'"
                                    >{{ performerInitial(item.creator_name) }}</span>
                                </div>

                                <!-- content -->
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-start justify-between gap-3 mb-1">
                                        <div>
                                            <p class="text-white/40 text-[11px] uppercase tracking-wider mb-0.5">Sponsored</p>
                                            <h3 class="text-white/90 text-sm font-semibold leading-snug line-clamp-2">{{ item.title }}</h3>
                                        </div>
                                        <span class="shrink-0 text-white/20 text-[11px] tabular-nums">{{ timeAgo(item.created_at) }}</span>
                                    </div>

                                    <div class="flex items-center gap-2 flex-wrap mt-1">
                                        <p :class="['text-xs truncate tracking-wide shrink-0', item.audition_rank === 1 ? 'text-yellow-400/70' : 'text-blue-400/80']">
                                            {{ item.creator_name }}
                                        </p>
                                        <span :class="['text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-wide shrink-0', releaseBadgeClass(item)]">
                                            {{ releaseLabel(item) }}
                                        </span>
                                    </div>

                                    <!-- funding bar -->
                                    <div class="mt-3">
                                        <div class="h-1 bg-white/5 rounded-full overflow-hidden">
                                            <div
                                                class="h-full rounded-full transition-all duration-700"
                                                :class="isFunded(item) ? 'bg-gradient-to-r from-yellow-500 to-yellow-300' : 'bg-gradient-to-r from-blue-600 to-blue-500'"
                                                :style="{ width: fundingPercent(item.likes, item.star_goal) + '%' }"
                                            ></div>
                                        </div>
                                    </div>

                                    <!-- footer row -->
                                    <div class="flex items-center gap-2 flex-wrap mt-2">
                                        <div class="flex items-center gap-1">
                                            <img :src="STAR_ICON_URL" alt="star" class="w-3 h-3" />
                                            <span class="text-white/70 text-xs font-bold tabular-nums">{{ formatNumber(item.stars_given) }}</span>
                                            <span class="text-white/30 text-xs">given</span>
                                        </div>
                                        <template v-if="item.star_goal && fundingPercent(item.stars_given, item.star_goal) >= 1">
                                            <span class="text-white/15 text-xs">·</span>
                                            <span class="text-emerald-400/80 text-xs tabular-nums">
                                                {{ fundingPercent(item.stars_given, item.star_goal) }}% of goal
                                            </span>
                                        </template>
                                        <template v-if="item.audition_rank">
                                            <span class="text-white/15 text-xs">·</span>
                                            <span v-if="item.audition_rank === 1" class="flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full top-sponsor-badge">
                                                <Trophy class="w-3 h-3 shrink-0" /> Top Sponsor
                                            </span>
                                            <span v-else-if="item.audition_rank === 2" class="flex items-center gap-1 bg-slate-400/10 border border-slate-400/30 text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                <Award class="w-3 h-3 shrink-0" /> Top #2
                                            </span>
                                            <span v-else-if="item.audition_rank === 3" class="flex items-center gap-1 bg-orange-700/10 border border-orange-700/30 text-orange-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                <Award class="w-3 h-3 shrink-0" /> Top #3
                                            </span>
                                            <span v-else class="text-blue-400/80 text-xs font-semibold tabular-nums">Top #{{ item.audition_rank }}</span>
                                        </template>
                                        <a
                                            v-if="item.scene_slug"
                                            :href="route(routePrefix + '.scene.show', item.scene_slug)"
                                            @click.stop
                                            class="ml-auto flex items-center gap-1 text-green-400 hover:text-green-300 text-[11px] font-semibold transition-colors"
                                        >
                                            <Film class="w-3 h-3 shrink-0" /> Watch Scene
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- ── Comment ── -->
                        <div
                            v-else-if="item._type === 'comment'"
                            class="ml-6 group relative"
                        >
                            <!-- connector line from left edge up to the card -->
                            <div class="absolute -left-4 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/30 to-transparent"></div>
                            <div class="absolute -left-[17px] top-3.5 w-3 h-px bg-blue-500/30"></div>

                            <div class="bg-[#0a0a12] border border-blue-500/15 hover:border-blue-500/30 rounded-xl overflow-hidden transition-all duration-150">
                                <!-- audition reference strip -->
                                <div class="flex items-center gap-2 px-4 py-2.5 bg-blue-500/5 border-b border-blue-500/10">
                                    <MessageCircle class="w-3 h-3 text-blue-400/60 shrink-0" />
                                    <p class="text-[11px] text-blue-300/50 truncate flex-1 leading-none">
                                        <span class="text-blue-300/80 font-medium">{{ item.feed_post?.title ?? 'an audition' }}</span>
                                        <span v-if="item.feed_post?.creator_name"> · {{ item.feed_post.creator_name }}</span>
                                    </p>
                                    <span class="shrink-0 text-white/20 text-[11px] tabular-nums">{{ timeAgo(item.created_at) }}</span>
                                </div>

                                <!-- comment text -->
                                <p class="px-4 py-3 text-white/70 text-sm leading-relaxed">{{ item.comment }}</p>
                            </div>
                        </div>

                    </template>
                </div>

                <!-- empty state -->
                <div v-else-if="!isActivityLoading" class="flex flex-col items-center justify-center py-20 text-center">
                    <div class="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                        <img :src="STAR_ICON_URL" alt="" class="w-7 h-7 opacity-20" />
                    </div>
                    <p class="text-white/25 text-sm">No activity yet.</p>
                </div>

                <!-- both sentinels remain — each triggers its own paginator independently -->
                <div ref="auditionsSentinel" class="h-1"></div>
                <div ref="commentsSentinel" class="h-1"></div>

                <!-- unified loading + error states -->
                <div v-if="isActivityLoading" class="flex justify-center py-8">
                    <div class="w-6 h-6 rounded-full border-2 border-white/10 border-t-blue-500 animate-spin"></div>
                </div>
                <div v-if="auditionsError" class="flex items-center justify-between gap-3 px-4 py-3 mt-2 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p class="text-red-400 text-sm">Failed to load more sponsored auditions.</p>
                    <button @click="loadMoreAuditions" class="shrink-0 text-xs font-semibold text-red-400 hover:text-red-300 underline underline-offset-2 transition-colors">Retry</button>
                </div>
                <div v-if="commentsError" class="flex items-center justify-between gap-3 px-4 py-3 mt-2 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p class="text-red-400 text-sm">Failed to load more comments.</p>
                    <button @click="loadMoreComments" class="shrink-0 text-xs font-semibold text-red-400 hover:text-red-300 underline underline-offset-2 transition-colors">Retry</button>
                </div>
            </section>

            <!-- ── REQUESTED CASTINGS ─────────────────────────────── -->
            <section class="mt-10">
                <!-- View all CTA -->
                <a
                    v-if="requestedCastings.filter(c => !c.deleted).length"
                    :href="route(`${routePrefix}.profile.my.castings`, { slug: profile.slug })"
                    class="mb-6 flex items-center justify-between gap-3 w-full rounded-xl border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 hover:border-purple-400/50 px-5 py-4 transition-all group"
                >
                    <div class="flex items-center gap-3">
                        <div class="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
                            <Clapperboard class="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                            <p class="text-white text-sm font-semibold leading-tight">View all your pitches</p>
                            <p class="text-purple-300/60 text-xs mt-0.5">Track progress, supporters &amp; production status</p>
                        </div>
                    </div>
                    <ChevronRight class="w-4 h-4 text-purple-400/60 group-hover:text-purple-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </a>

                <div class="flex items-center gap-3 mb-6">
                    <span class="rounded bg-[#141414]/20 border border-purple-600 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                        Pitch Requests
                    </span>
                    <span class="text-white/30 text-xs">{{ requestedCastings.filter(c => !c.deleted).length }} request{{ requestedCastings.filter(c => !c.deleted).length !== 1 ? 's' : '' }}</span>
                </div>

                <div v-if="requestedCastings.filter(c => !c.deleted).length" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <CastingCard
                        v-for="casting in requestedCastings.filter(c => !c.deleted)"
                        :key="casting.id"
                        :casting="casting"
                        :routePrefix="routePrefix"
                        mode="requested"
                    />
                </div>

                <!-- Empty state -->
                <div v-else class="flex flex-col items-center justify-center py-12 px-6 rounded-xl bg-zinc-900/40 border border-white/[0.06]">
                    <div class="w-16 h-16 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-4">
                        <Clapperboard class="w-8 h-8 text-purple-400/60" />
                    </div>
                    <h3 class="text-white text-lg font-bold mb-1">My Scenes</h3>
                    <p class="text-white/50 text-sm text-center max-w-md leading-relaxed mb-6">
                        You have not created any scenes yet. Submit your pitch and start your studio.
                    </p>
                    <Link
                        :href="getCastingsUrl(isUserLoggedIn, routePrefix) + '&action=create-scene'"
                        class="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg transition-all active:scale-95 shadow-lg shadow-purple-600/20"
                    >
                        Create Your First Pitch
                    </Link>
                </div>

            </section>

            <!-- ── SUPPORTED CASTINGS ──────────────────────────────── -->
            <section v-if="supportedCastings.length" class="mt-10">
                <div class="flex items-center gap-3 mb-6">
                    <span class="rounded bg-[#141414]/20 border border-amber-500 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                        Pitches Supported
                    </span>
                    <span class="text-white/30 text-xs">{{ supportedCastings.length }} casting{{ supportedCastings.length !== 1 ? 's' : '' }}</span>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <CastingCard
                        v-for="casting in supportedCastings"
                        :key="casting.casting_id"
                        :casting="casting"
                        :routePrefix="routePrefix"
                        mode="backed"
                    />
                </div>
            </section>

            </div>
            <!-- end MAIN COLUMN -->

            <!-- ── SIDEBAR — Discover Auditions ───────────────────── -->
            <aside
                v-if="pendingAuditions.length || discoverCastings.length || !isUserLoggedIn"
                class="hidden lg:block w-72 xl:w-80 shrink-0 sticky top-[72px] self-start"
            >
                <!-- divider line to visually separate from profile content -->
                <div class="border-l border-white/[0.06] pl-8">

                    <!-- ── Guest CTA card ── -->
                    <div
                        v-if="!isUserLoggedIn"
                        class="relative overflow-hidden rounded-2xl border border-blue-500/20 bg-gradient-to-b from-blue-950/50 to-black/60 p-5 mb-6"
                    >
                        <!-- top accent line -->
                        <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
                        <!-- radial glow -->
                        <div class="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(37,99,235,0.08),transparent)] pointer-events-none"></div>

                        <div class="relative flex flex-col gap-4">
                            <!-- icon + heading -->
                            <div class="flex items-start gap-3">
                                <div class="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                                    <img :src="STAR_ICON_URL" alt="star" class="w-4 h-4" />
                                </div>
                                <div>
                                    <p class="text-white text-sm font-semibold leading-snug">Build a profile like {{ profile.name }}'s</p>
                                    <p class="text-white/35 text-xs mt-0.5 leading-relaxed">Sponsor auditions and earn your place on the leaderboard.</p>
                                </div>
                            </div>

                            <!-- actions -->
                            <div class="flex flex-col gap-2">
                                <button
                                    @click="handleJoinClick('MyPage Sidebar Join Clicked')"
                                    type="button"
                                    class="w-full h-9 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white text-xs font-bold transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center"
                                >
                                    Join Free
                                </button>
                                <a
                                    :href="route('members2.auth.login')"
                                    class="w-full h-9 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-white/50 hover:text-white/80 text-xs font-semibold transition-all flex items-center justify-center"
                                >
                                    Sign In
                                </a>
                            </div>
                        </div>
                    </div>

                    <template v-if="pendingAuditions.length">
                        <div class="flex items-center justify-between mb-5">
                            <p class="text-white/50 text-xs font-semibold uppercase tracking-widest">
                                Discover Auditions
                            </p>
                            <button
                                type="button"
                                @click="goToAuditionsLanding()"
                                class="text-blue-400/60 hover:text-blue-400 text-[11px] font-semibold uppercase tracking-wide transition-colors"
                            >See all →</button>
                        </div>

                        <p class="text-white/25 text-xs leading-relaxed mb-5">
                            These auditions are still seeking sponsors — be part of making them happen.
                        </p>

                        <div class="space-y-3">
                            <div
                                v-for="audition in pendingAuditions"
                                :key="audition.slug"
                                @click="navigateToAudition(audition.slug)"
                                class="group flex flex-col cursor-pointer gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-blue-600/30 transition-all duration-150"
                            >
                                <div>
                                    <p class="text-white/80 text-xs font-semibold leading-snug line-clamp-2 group-hover:text-white transition-colors">
                                        {{ audition.title }}
                                    </p>
                                    <p class="text-blue-400/60 text-[11px] mt-0.5 truncate">{{ audition.creator_name }}</p>
                                </div>

                                <!-- mini funding bar -->
                                <div>
                                    <div class="h-0.5 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            class="h-full rounded-full bg-gradient-to-r from-blue-600 to-blue-500"
                                            :style="{ width: fundingPercent(audition.likes, audition.star_goal) + '%' }"
                                        ></div>
                                    </div>
                                    <div class="flex items-center justify-between mt-1.5">
                                        <span class="text-white/20 text-[10px] tabular-nums">
                                            {{ formatNumber(audition.likes) }} / {{ formatNumber(audition.star_goal) }}
                                            <img :src="STAR_ICON_URL" alt="star" class="w-2.5 h-2.5 inline-block align-middle" />
                                        </span>
                                        <span class="text-white/20 text-[10px] tabular-nums">
                                            {{ fundingPercent(audition.likes, audition.star_goal) }}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- ── Discover Castings ── -->
                    <template v-if="discoverCastings.length">
                        <div class="flex items-center justify-between mt-8 mb-5">
                            <p class="text-white/50 text-xs font-semibold uppercase tracking-widest">
                                Discover Pitches
                            </p>
                            <Link
                                :href="getCastingsUrl(isUserLoggedIn, routePrefix)"
                                class="text-purple-400/60 hover:text-purple-400 text-[11px] font-semibold uppercase tracking-wide transition-colors"
                            >See all →</Link>
                        </div>

                        <p class="text-white/25 text-xs leading-relaxed mb-5">
                            These pitches requests are open — support them with your stars.
                        </p>

                        <div class="space-y-3">
                            <div
                                v-for="casting in discoverCastings"
                                :key="casting.id"
                                class="group flex flex-col gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-purple-600/30 transition-all duration-150"
                            >
                                <!-- performer thumbnails -->
                                <div class="flex gap-1.5">
                                    <a
                                        v-for="performer in casting.performers.slice(0, 4)"
                                        :key="performer.id"
                                        :href="performer.slug ? route(`${routePrefix}.performer.show`, { slug: performer.slug }) : null"
                                        class="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 ring-1 ring-zinc-900 flex-shrink-0 hover:ring-white/30 transition-all"
                                    >
                                        <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top" loading="lazy" />
                                    </a>
                                    <div
                                        v-if="casting.performers.length > 4"
                                        class="w-7 h-7 rounded-full bg-zinc-800 ring-1 ring-zinc-900 flex items-center justify-center flex-shrink-0"
                                    >
                                        <span class="text-white/50 text-[10px] font-bold">+{{ casting.performers.length - 4 }}</span>
                                    </div>
                                </div>

                                <!-- title + performers -->
                                <div>
                                    <p class="text-white/80 text-xs font-semibold leading-snug line-clamp-2 group-hover:text-white transition-colors">
                                        {{ casting.title }}
                                    </p>
                                    <p class="text-purple-400/60 text-[11px] mt-0.5 truncate">{{ casting.performers.map(p => p.name).join(', ') }}</p>
                                </div>

                                <!-- progress bar -->
                                <div v-if="casting.star_goal > 0">
                                    <div class="flex items-center justify-between mb-1">
                                        <span v-if="casting.stars_received / casting.star_goal >= 0.75" class="text-[10px] font-semibold text-orange-400">🔥 Trending</span>
                                        <span v-else class="text-[10px] text-white/30"></span>
                                        <span class="text-[10px] text-white/40 font-medium">{{ formatNumber(casting.stars_received) }} / {{ formatNumber(casting.star_goal) }}</span>
                                    </div>
                                    <div class="h-1 bg-white/[0.08] rounded-full overflow-hidden">
                                        <div
                                            class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                                            :style="{ width: Math.min(100, Math.round(casting.stars_received / casting.star_goal * 100)) + '%' }"
                                        />
                                    </div>
                                </div>

                                <!-- stats row -->
                                <div class="flex items-center gap-2">
                                    <span v-if="casting.backers_count > 0" class="flex items-center gap-1 text-white/30 text-[10px]">
                                        <Users class="w-3 h-3" />
                                        {{ casting.backers_count }}
                                    </span>
                                    <span v-if="casting.stars_received > 0" class="flex items-center gap-1 text-white/30 text-[10px]">
                                        <img :src="STAR_ICON_URL" alt="star" class="w-2.5 h-2.5" />
                                        {{ formatNumber(casting.stars_received) }}
                                    </span>
                                    <span v-if="casting.format" class="text-white/20 text-[10px] ml-auto">{{ casting.format }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                </div>
            </aside>
            <!-- end SIDEBAR -->

            </div>
            <!-- end MAIN + SIDEBAR LAYOUT -->

            <!-- ── GUEST INLINE CTA ───────────────────────────────── -->
            <div
                v-if="!isUserLoggedIn"
                class="mt-16 mx-4 md:mx-8 lg:mx-16 max-w-[1400px] lg:mx-auto"
            >
                <div class="relative overflow-hidden rounded-2xl border border-blue-600/30 bg-gradient-to-br from-blue-950/60 via-[#000d2e]/80 to-black px-6 py-8 text-center">
                    <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>

                    <p class="text-white/50 text-xs uppercase tracking-widest mb-2">Want to do the same?</p>
                    <h2 class="text-white text-xl md:text-2xl font-bold mb-2">
                        Sponsor auditions. Earn your credit.
                    </h2>
                    <p class="text-white/50 text-sm max-w-md mx-auto mb-6 leading-relaxed">
                        Members like <span class="text-white/80 font-semibold">{{ profile.name }}</span> use Stars to sponsor auditions and help bring scenes to life. Join free and start building your own profile.
                    </p>

                    <div class="flex items-center justify-center gap-3 flex-wrap">
                        <button
                            @click="handleJoinClick('MyPage Bottom Join Clicked')"
                            type="button"
                            class="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white text-sm font-bold transition-all shadow-lg shadow-blue-600/25 flex items-center justify-center"
                        >
                            Join Free
                        </button>
                        <a
                            :href="route('members2.auth.login')"
                            class="h-11 px-6 rounded-xl bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 text-white/70 hover:text-white text-sm font-semibold transition-all flex items-center justify-center"
                        >
                            Sign In
                        </a>
                    </div>
                </div>
            </div>

            <!-- ── FOOTER ─────────────────────────────────────────── -->
            <hr class="border border-[#1a1a1a] mt-20">
            <footer class="mt-8 px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8">
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">About</h3>
                        <p class="text-white/25 text-xs leading-relaxed">
                            With a history of over 25 years, Naughty America is recognized as one of the most
                            award-winning adult entertainment platforms online.
                        </p>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Explore</h3>
                        <div class="grid grid-cols-2 gap-y-2 text-xs">
                            <a :href="route('www.main.home')" class="text-white/25 hover:text-white/60 transition-colors">Home</a>
                            <a :href="route('www.scene.index')" class="text-white/25 hover:text-white/60 transition-colors">Videos</a>
                            <a :href="route('www.join.signUp')" class="text-white/25 hover:text-white/60 transition-colors">Join</a>
                            <a :href="route('www.fantasy.index')" class="text-white/25 hover:text-white/60 transition-colors">Categories</a>
                            <a :href="route('www.performer.index', 'top-rated')" class="text-white/25 hover:text-white/60 transition-colors">Pornstars</a>
                            <a :href="route('www.site.index')" class="text-white/25 hover:text-white/60 transition-colors">Sites</a>
                        </div>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Legal</h3>
                        <div class="space-y-2 text-xs">
                            <a :href="route('www.main.privacyPolicy')" class="block text-white/25 hover:text-white/60 transition-colors">Privacy Policy</a>
                            <a :href="route('www.main.termsOfService')" class="block text-white/25 hover:text-white/60 transition-colors">Terms of Service</a>
                            <a :href="route('www.main.usc2257')" class="block text-white/25 hover:text-white/60 transition-colors">18 USC 2257</a>
                        </div>
                    </div>
                </div>
                <div class="border-t border-white/5 py-6 flex items-center justify-between text-[11px] text-white/15">
                    <span>&copy; {{ new Date().getFullYear() }} La Touraine, Inc.</span>
                    <a class="rta-label" href="http://www.rtalabel.org/" target="_blank" rel="noopener">
                        <img width="60" height="28" src="https://sm.naughtycdn.com/images/members2/rta-light.png" alt="RTA" />
                    </a>
                </div>
            </footer>

            <!-- ── GUEST STICKY BOTTOM BAR ────────────────────────── -->
            <Transition
                enter-active-class="transition-transform duration-300 ease-out"
                enter-from-class="translate-y-full"
                enter-to-class="translate-y-0"
                leave-active-class="transition-transform duration-200 ease-in"
                leave-from-class="translate-y-0"
                leave-to-class="translate-y-full"
            >
                <div
                    v-if="!isUserLoggedIn && showGuestCta"
                    class="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10"
                >
                    <div class="px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] max-w-[1400px] mx-auto flex items-center gap-3">
                        <div class="flex-1 min-w-0">
                            <p class="text-white text-sm font-semibold leading-tight truncate">
                                Build a profile like {{ profile.name }}'s
                            </p>
                            <p class="text-white/40 text-xs mt-0.5">
                                Sponsor auditions and earn your place in the leaderboard.
                            </p>
                        </div>
                        <div class="flex items-center gap-2 shrink-0">
                            <button
                                @click="handleJoinClick('MyPage Sticky Bottom Join Clicked')"
                                type="button"
                                class="h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white text-xs font-bold transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center"
                            >
                                Join Free
                            </button>
                            <a
                                :href="route('members2.auth.login')"
                                class="h-9 px-4 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] border border-white/10 text-white/70 hover:text-white text-xs font-semibold transition-all flex items-center justify-center"
                            >
                                Sign In
                            </a>
                        </div>
                    </div>
                </div>
            </Transition>

            <StarPowerModal :isOpen="isStarPowerModalOpen"
                            :source="'Public_Auditions_Profile'"
                            @close="isStarPowerModalOpen = false"
            />

            <BuyStarsModal
                :isOpen="isBuyStarsModalOpen"
                :userBalance="walletBalance"
                :starPacks="props.starPacks"
                @close="isBuyStarsModalOpen = false"
                @buy="handleBuyStarPack"
            />

            <SharePopup />

        </div>
    </div>
</template>

<style>
/* Hide legacy site chrome — same pattern as reels-tag-page */
.my-page-body #desktop-header { display: none; }
.my-page-body #main { margin-top: 0 !important; margin-bottom: 0; overflow: visible !important; }
.my-page-body #masterlayout { overflow: visible !important; }
.my-page-body #nprogress .bar { display: none; }
</style>

<style scoped>
/* Modal transition */
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from,  .modal-leave-to      { opacity: 0; }

/* Shoutout spotlight fade transition */
.shoutout-fade-enter-active, .shoutout-fade-leave-active { transition: opacity 0.4s ease, transform 0.4s ease; }
.shoutout-fade-enter-from, .shoutout-fade-leave-to       { opacity: 0; transform: translateY(8px); }
.modal-enter-active > div, .modal-leave-active > div { transition: transform 0.2s ease; }
.modal-enter-from   > div                { transform: translateY(16px) scale(0.98); }
.modal-leave-to     > div                { transform: translateY(8px)  scale(0.99); }

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* hide the native scrollbar on the performers carousel */
.performers-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.performers-scroll::-webkit-scrollbar {
    display: none;
}

/* ── Top Sponsor card — pulsing golden glow ─────────────────── */
@keyframes gold-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(234, 179, 8, 0), 0 0 0 0 rgba(234, 179, 8, 0); }
    50%       { box-shadow: 0 0 18px 2px rgba(234, 179, 8, 0.18), 0 0 40px 8px rgba(234, 179, 8, 0.06); }
}
.top-sponsor-card {
    animation: gold-pulse 2.8s ease-in-out infinite;
}

/* ── Shimmer sweep across the card header ───────────────────── */
@keyframes shimmer-move {
    0%   { transform: translateX(-100%) skewX(-12deg); opacity: 0; }
    20%  { opacity: 1; }
    80%  { opacity: 1; }
    100% { transform: translateX(200%) skewX(-12deg); opacity: 0; }
}
.shimmer-sweep {
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(234, 179, 8, 0.06) 40%,
        rgba(234, 179, 8, 0.14) 50%,
        rgba(234, 179, 8, 0.06) 60%,
        transparent 100%
    );
    animation: shimmer-move 3.5s ease-in-out infinite;
}

/* ── Shoutout scroll strip ───────────────────────────────────── */
.shoutouts-scroll { scrollbar-width: none; }
.shoutouts-scroll::-webkit-scrollbar { display: none; }

/* ── Shoutout card — soft gold breathe ──────────────────────── */
@keyframes shoutout-breathe {
    0%, 100% { box-shadow: 0 0 0 0 rgba(200,168,75,0), 0 8px 40px -8px rgba(0,0,0,0.6); }
    50%       { box-shadow: 0 0 30px 4px rgba(200,168,75,0.08), 0 8px 40px -8px rgba(0,0,0,0.6); }
}
.shoutout-card {
    animation: shoutout-breathe 4s ease-in-out infinite;
}
.shoutout-message {
    text-shadow: 0 1px 12px rgba(200,168,75,0.08);
}

/* ── Top Sponsor badge — gentle float ───────────────────────── */
@keyframes badge-float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-2px); }
}
.top-sponsor-badge {
    animation: badge-float 2s ease-in-out infinite;
}
</style>
